import { z } from 'zod';

import { zodDateOrString } from './utils/Zod';

export interface ResourceScopeConfig<TData, TInput = TData> {
  dataSchema: z.ZodType<TData, TInput>;
  defaultData: TData;
}

/**
 * Canonical resource scope definitions shared between backend and frontend.
 *
 * All change-ID scopes use z.coerce.bigint() so that both native BigInt values
 * and plain numbers (from JSON.parse or Prisma Int columns) are accepted and
 * coerced to bigint. This is safe because these values represent PostgreSQL
 * change IDs, not values near Number.MAX_SAFE_INTEGER.
 *
 * TODO (#10340): migrate Prisma Int changeId columns to BigInt, then remove
 * Number() conversions at store call sites.
 */

const DEFAULT_CHANGE_ID_SCOPE_CONFIG = {
  dataSchema: z.object({ highestProcessedChangeId: z.coerce.bigint() }),
  defaultData: { highestProcessedChangeId: 0n },
};

const DEFAULT_TICKER_SCOPE_CONFIG = {
  dataSchema: z.object({ lastTickedAt: zodDateOrString }),
  defaultData: { lastTickedAt: new Date(0) },
};

export const RESOURCE_SCOPES = {
  externalLoanIdTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  loanNoteTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  loanHistoryTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  pointOfContactTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  auditLogTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  paymentTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  paymentDueTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  usioPaymentAccountTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  repayPaymentAccountTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  taskTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  userTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  companyTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  companyAuditLogTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  indexRateTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,

  loanTicker: DEFAULT_TICKER_SCOPE_CONFIG,
  workflowTicker: DEFAULT_TICKER_SCOPE_CONFIG,

  loanTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  loanSnapshotTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  borrowerTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  authorizedContactTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  transactionTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  workflowInstanceTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
  achBatchTransactionTable: DEFAULT_CHANGE_ID_SCOPE_CONFIG,
} as const satisfies Record<string, ResourceScopeConfig<any, any>>;

export type ResourceScope = keyof typeof RESOURCE_SCOPES;
export type ResourceData<T extends ResourceScope> = z.infer<(typeof RESOURCE_SCOPES)[T]['dataSchema']>;

/** Lookup map of scope -> dataSchema, for frontend validation of JSON editor input. */
export const RESOURCE_SCOPE_DATA_SCHEMAS: Record<string, z.ZodType<unknown>> = Object.fromEntries(
  Object.entries(RESOURCE_SCOPES).map(([scope, config]) => [scope, config.dataSchema]),
);
